import { isNonEmpty } from '@sgme/fp';
import type { EditRunResponse } from 'apiConnector/scheduledRun/scheduledRuns';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { VerticalFormGroup } from 'components/shared/FormGroup';
import { ErrorAlert } from 'components/shared/ScheduledRun/ErrorAlert';
import { ScheduledRunModalFooter } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalFooter';
import { ScheduledRunModalHeader } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalHeader';
import { ScheduleStatusModal } from 'components/shared/ScheduledRun/Modal/ScheduleStatusModal';
import { ConnectedScheduleDays } from 'components/shared/ScheduledRun/ScheduleDays';
import { ConnectedScheduleRange } from 'components/shared/ScheduledRun/ScheduleRange';
import { ConnectedReadersPicker } from 'components/shared/ScheduledRun/shared/ReadersPicker';
import type { IdOwnProps } from 'components/shared/ScheduledRun/type';
import { TextArea } from 'components/shared/TextField/TextArea';
import { TimePicker } from 'components/shared/TimePicker';
import { useNavigateTo } from 'hooks/useNavigateTo';
import type { FormError } from 'models/formError';
import { Modal, ModalBody } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MapStateToProps } from 'react-redux';
import { connect, type MapDispatchToPropsFunction } from 'react-redux';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { getScheduledFieldsErrors } from 'state/scheduledRuns/selectors';
import type { ScheduledRun, ScheduledRunFields, TriggerMode } from 'state/scheduledRuns/state';
import { TRIGGER_MODES } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import type { TypedError } from 'utils/errors/errors';
import { asError, isError, type LoadableContent } from 'utils/status';

interface DispatchProps {
  onClosed: () => void;

  onCreationRequested: () => void;
  onTitleChanged: (title?: string) => void;
  onCommentChanged: (comment?: string) => void;
  onTimeChanged: (time?: string) => void;
  onTriggerModeChanged: (triggerMode: TriggerMode) => void;
}
type StateProps = {
  title?: string;
  comment?: string;
  triggerMode: TriggerMode;

  time?: string;

  errors: readonly FormError<ScheduledRunFields>[];

  timeZone: string;

  createRun: LoadableContent<EditRunResponse | undefined, TypedError>;
};

type Props = DispatchProps & StateProps & IdOwnProps;

const mapDispatchProps: MapDispatchToPropsFunction<DispatchProps, IdOwnProps> = (dispatch, { id }) => ({
  onClosed() {
    dispatch(scheduledRunActions.resetScheduleType(id));
  },
  onTitleChanged(title) {
    dispatch(scheduledRunActions.titleChanged(id, title));
  },
  onTimeChanged(time) {
    dispatch(scheduledRunActions.timeChanged(id, time));
  },
  onCommentChanged(comment) {
    dispatch(scheduledRunActions.commentChanged(id, comment));
  },
  onTriggerModeChanged(triggerMode) {
    dispatch(scheduledRunActions.triggerModeChanged(id, triggerMode));
  },
  onCreationRequested() {
    dispatch(scheduledRunActions.scheduledRunCreationRequested(id));
  },
});

const mapStateToProps: MapStateToProps<StateProps, IdOwnProps, AppState> = (app, { id }) => ({
  title: app.scheduledRun.runs[id].title,
  comment: app.scheduledRun.runs[id].comment,
  triggerMode: app.scheduledRun.runs[id].triggerMode,
  time: app.scheduledRun.runs[id].time,
  errors: getScheduledFieldsErrors(app, id),
  timeZone: app.scheduledRun.timeZone,
  createRun: app.scheduledRun.runs[id]?.createRun,
});
const modalId = 'new-schedule-modal';
function NewScheduleModal({
  onClosed,
  title,
  comment,
  onTitleChanged,
  onCommentChanged,
  triggerMode,
  onTriggerModeChanged,
  onTimeChanged,
  id,
  time,
  timeZone,
  onCreationRequested,
  errors,
  createRun,
}: Props) {
  const navigateToScheduledQuotesBlotter = useNavigateTo('/scheduled-quotes-blotter');
  const { formatMessage } = useIntl();
  const disableForm = createRun.status === 'loading';

  return createRun.status === 'success' && isNonEmpty(title) ? (
    <ScheduleStatusModal
      content={
        <FormattedMessage id={'scheduledRun.processDone.modal.addToNewSchedule.description'} values={{ name: title }} />
      }
      rightBtn={'scheduledRun.processDone.modal.seeScheduledQuotes.button'}
      title="scheduledRun.processDone.modal.addToNewSchedule.title"
      icon="check"
      color="success"
      onClosed={onClosed}
      onLeftClicked={onClosed}
      onRightClicked={() => {
        onClosed();
        navigateToScheduledQuotesBlotter();
      }}
    />
  ) : (
    <Modal show onHide={onClosed} id={modalId} itemID={modalId}>
      <ScheduledRunModalHeader
        title="scheduledRun.addToNewSchedule.modal"
        loadingStatus={createRun.status}
        onClosed={onClosed}
      />
      <ModalBody className="overflow-visible pb-2">
        <WithInvalidFieldsContext validationErrors={errors}>
          <VerticalFormGroup<keyof ScheduledRunFields> inputId={'title'} label="scheduledRun.title" errorPrefix="">
            <input
              className="form-control"
              value={title}
              onChange={e => onTitleChanged(e.target.value)}
              disabled={disableForm}
            />
          </VerticalFormGroup>
          <VerticalFormGroup<keyof ScheduledRun> inputId={'type'} label="scheduledRun.type" errorPrefix="">
            <ButtonsGroup
              isDisabled={disableForm}
              isRadio
              translationPrefix="scheduledRun.triggerType"
              items={TRIGGER_MODES}
              selectedItems={triggerMode}
              onSelectionChanged={selection => onTriggerModeChanged(selection[0])}
            />
          </VerticalFormGroup>
          {triggerMode === 'days' && <ConnectedScheduleDays id={id} disabled={disableForm} />}
          {triggerMode === 'range' && <ConnectedScheduleRange id={id} disabled={disableForm} />}
          <VerticalFormGroup<keyof ScheduledRunFields>
            inputId={'time'}
            labelText={formatMessage({ id: 'scheduledRun.time' }, { timeZone })}
            errorPrefix=""
          >
            <TimePicker
              disabled={disableForm}
              type="string"
              value={time}
              onValueChanged={onTimeChanged}
              format={24}
              minuteStep={15}
            />
          </VerticalFormGroup>
          <VerticalFormGroup<keyof ScheduledRunFields>
            inputId={'comment'}
            labelText={formatMessage({ id: 'scheduledRun.comment' })}
            errorPrefix=""
          >
            <TextArea
              disabled={disableForm}
              value={comment}
              onValueChanged={onCommentChanged}
              className="form-control"
              placeholder={formatMessage({ id: 'scheduledRun.comment' })}
            />
          </VerticalFormGroup>
          <VerticalFormGroup<keyof ScheduledRunFields>
            inputId="readers"
            label="scheduledRun.distributionList"
            errorPrefix=""
          >
            <ConnectedReadersPicker id={id} disabled={disableForm} />
          </VerticalFormGroup>
        </WithInvalidFieldsContext>
        {isError(createRun) && <ErrorAlert {...createRun.error} />}
      </ModalBody>
      <ScheduledRunModalFooter
        error={asError(createRun)?.error}
        cancelBtn={
          <button
            className="btn btn-outline-secondary btn-outline btn-lg flex-grow-1 me-1  "
            data-e2e="new-scheduled-run-cancel-button"
            onClick={onClosed}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </button>
        }
        validateBtn={
          <button
            disabled={errors.length > 0 || disableForm}
            onClick={onCreationRequested}
            className="btn btn-primary btn-lg flex-grow-1 ms-1 align-items-center "
            data-e2e="new-scheduled-run-create-button"
          >
            <FormattedMessage id="create" />
          </button>
        }
        modalId={modalId}
        hasHelpBtn={isError(createRun)}
      />
    </Modal>
  );
}

export const ConnectedNewScheduleModal = connect(mapStateToProps, mapDispatchProps)(NewScheduleModal);
